import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Star, Camera, Film, FileText } from 'lucide-react';
import './StopScrapbook.css';

// Renders a single entry. Supports photos, videos (file or YouTube/Vimeo URL),
// and free-form notes / blog posts. Designed so users could append any of
// these to a stop's `entries` array.
function ScrapEntry({ entry }) {
  if (entry.type === 'photo') {
    return (
      <figure className="stop-scrap-entry stop-scrap-photo">
        <img src={entry.url} alt={entry.caption || ''} />
        {entry.caption && <figcaption>{entry.caption}</figcaption>}
      </figure>
    );
  }

  if (entry.type === 'video') {
    const url = entry.url || '';
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
    return (
      <figure className="stop-scrap-entry stop-scrap-video">
        {ytMatch ? (
          <div className="stop-scrap-video-frame">
            <iframe
              src={`https://www.youtube.com/embed/${ytMatch[1]}`}
              title={entry.caption || 'Video'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              frameBorder="0"
            />
          </div>
        ) : (
          <video controls poster={entry.poster} preload="metadata">
            <source src={url} />
            Your browser does not support video playback.
          </video>
        )}
        {entry.caption && <figcaption>{entry.caption}</figcaption>}
      </figure>
    );
  }

  if (entry.type === 'note') {
    return (
      <article className="stop-scrap-entry stop-scrap-note">
        {entry.title && <h4>{entry.title}</h4>}
        <p>{entry.body}</p>
      </article>
    );
  }

  return null;
}

function entrySummary(entries = []) {
  const counts = entries.reduce((acc, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    return acc;
  }, {});
  const parts = [];
  if (counts.photo) parts.push({ icon: Camera, label: `${counts.photo} photo${counts.photo > 1 ? 's' : ''}` });
  if (counts.video) parts.push({ icon: Film, label: `${counts.video} video${counts.video > 1 ? 's' : ''}` });
  if (counts.note) parts.push({ icon: FileText, label: `${counts.note} note${counts.note > 1 ? 's' : ''}` });
  return parts;
}

export default function StopScrapbook({ stop, onClose }) {
  // Close on Escape.
  useEffect(() => {
    if (!stop) return undefined;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [stop, onClose]);

  // Prevent background scroll while modal is open.
  useEffect(() => {
    if (!stop) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [stop]);

  return (
    <AnimatePresence>
      {stop && (
        <motion.div
          className="stop-scrap-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
        >
          <motion.div
            className="stop-scrap-modal"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            style={{ '--accent': stop.color }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="stop-scrap-accent" />

            <header className="stop-scrap-header">
              <div className="stop-scrap-header-icon">
                {stop.icon ? <stop.icon size={20} /> : null}
              </div>
              <div className="stop-scrap-header-text">
                <h2>{stop.title}</h2>
                <p className="stop-scrap-meta">
                  <MapPin size={12} /> {stop.city}
                  {stop.date && <> · {stop.date}</>}
                </p>
                {stop.rating && (
                  <div className="stop-scrap-rating">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        fill={i < stop.rating ? '#FFB400' : 'none'}
                        color={i < stop.rating ? '#FFB400' : '#cbd5e1'}
                        strokeWidth={2}
                      />
                    ))}
                    <span className="stop-scrap-rating-num">{stop.rating}.0</span>
                  </div>
                )}
              </div>
              <button className="stop-scrap-close" onClick={onClose} aria-label="Close">
                <X size={20} />
              </button>
            </header>

            {stop.description && (
              <p className="stop-scrap-blurb">{stop.description}</p>
            )}

            {stop.entries && stop.entries.length > 0 && (
              <div className="stop-scrap-summary">
                {entrySummary(stop.entries).map(({ icon: Icon, label }, i) => (
                  <span key={i} className="stop-scrap-chip">
                    <Icon size={12} /> {label}
                  </span>
                ))}
              </div>
            )}

            <div className="stop-scrap-body">
              {(stop.entries || []).map((entry, i) => (
                <ScrapEntry key={i} entry={entry} />
              ))}
              {(!stop.entries || stop.entries.length === 0) && (
                <div className="stop-scrap-empty">
                  <p>No entries yet. Add photos, videos, or a blog post for this stop.</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
